/* ============================================================
   Dathent — BillingStateBanner (TASK-331 phase 1)
   Persistent, app-wide notice for accessState in {grace, lapsed,
   cancel_scheduled}. Reads the shared DashboardDataProvider snapshot so
   billing mutations and later refreshes update the banner consistently.

   Mounted inside DashboardDataProvider + UpgradeProvider (see App.jsx) —
   NOT literally beside PendingOAuthBanner at the outer RequireActiveBilling
   return, because the Renew action reuses the shared UpgradeModal via
   useUpgrade(), and UpgradeModal itself reads useDashboardData(); either
   hook throws outside its provider. Still renders on every /app screen,
   which is what "persistent app-wide banner" requires.

   Renders no billing warning before loading or for other known states.
   A failed initial read offers an explicit read-only retry; it does not
   infer the account's plan. One primary CTA per state (owner directive).
   ============================================================ */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icons.jsx';
import { api } from '../services/api/client.js';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import { NEW_PLAN_DEFS } from '../lib/plans.js';
import { track } from '../lib/funnel.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { effectiveAccessState, canResumeSubscription, subscriptionResumeHasConverged } from '../lib/billingState.js';
import { freePublishingState } from '../lib/freePublishing.js';

function formatDate(value, locale) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale?.startsWith('en') ? 'en-GB' : (locale || 'en-GB'), {
      day: 'numeric', month: 'short', year: 'numeric',
    }).format(date);
  } catch (_) {
    return null;
  }
}

// Backend planKey -> display name, same new-grid/legacy disambiguation
// UpgradeModal/PaymentIssueNotice use. Falls back to a title-cased raw key.
function resolvePlanName(planKey, { getPlanDisplay, getNewPlanDisplay, newGridActive }) {
  if (!planKey) return null;
  const display = (newGridActive && NEW_PLAN_DEFS.some((p) => p.id === planKey))
    ? getNewPlanDisplay(planKey)
    : getPlanDisplay(planKey);
  return display?.name || String(planKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function BillingStateBanner() {
  const { t, i18n } = useTranslation();
  const { openUpgrade } = useUpgrade();
  const { getPlanDisplay, getNewPlanDisplay, newGridActive } = usePlanCatalog();
  const {
    entitlements,
    entitlementsError,
    refreshEntitlements,
    captureEntitlementsScope,
    isEntitlementsScopeCurrent,
    accountId,
    resumeSubmitted,
    isResumeSubmitted,
    markResumeSubmitted,
  } = useDashboardData();
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState(null);

  const accessState = entitlementsError ? null : effectiveAccessState(entitlements);
  const cancellationKey = accessState === 'cancel_scheduled'
    ? JSON.stringify([
      accountId || null,
      entitlements?.subscriptionId || null,
      entitlements?.currentPeriodEnd || null,
      entitlements?.nextInvoiceAt || null,
    ])
    : null;
  const [dismissedCancellationKey, setDismissedCancellationKey] = useState(null);

  useEffect(() => {
    if (accessState === 'grace' || accessState === 'lapsed' || accessState === 'cancel_scheduled') {
      try { track('billing_state_banner_shown', { accessState }); } catch { /* fail-silent */ }
    }
  }, [accessState]);

  useEffect(() => {
    setResuming(false);
    setResumeError(null);
    setDismissedCancellationKey(null);
  }, [accountId]);

  useEffect(() => {
    if (subscriptionResumeHasConverged(entitlements)) {
      setResumeError(null);
    }
  }, [entitlements]);

  useEffect(() => {
    if (!accessState) return;
    setDismissedCancellationKey((dismissedKey) => (
      accessState === 'cancel_scheduled' && dismissedKey === cancellationKey ? dismissedKey : null
    ));
  }, [accessState, cancellationKey]);

  if (entitlementsError) {
    const retryStatus = async () => {
      if (resuming) return;
      const scope = captureEntitlementsScope();
      setResuming(true);
      try {
        await refreshEntitlements({ scope });
      } finally {
        if (isEntitlementsScopeCurrent(scope)) setResuming(false);
      }
    };
    return (
      <section className="card flb-banner" role="status">
        <div className="flb-msg">{t('settings.billingStatusLoadError')}</div>
        <div className="flb-actions">
          <button type="button" className="btn btn-primary btn-sm" disabled={resuming} onClick={retryStatus}>
            {t('settings.refreshBillingStatus')}
          </button>
        </div>
      </section>
    );
  }

  // Before loading, or for a known state without an urgent notice, render
  // nothing. A17: 'free'/'new'/'trialing'/'active' all land here too.
  // MINOR 7 (round 7 review): 'downgrade_scheduled' (TASK-331 phase 2,
  // Subscription Schedules) is deliberately NOT in this allowlist — a
  // deviation from the design brief's own L363-364 wording, which grouped it
  // with grace/lapsed/cancel_scheduled as another persistent-banner state.
  // Unlike those three, nothing is ending or at risk here: the org keeps its
  // CURRENT (higher) plan/limits until the flip date, and the change is
  // trivially reversible (the undo CTA). A loud, sticky, app-wide banner for
  // a purely informational future plan change would read as more urgent than
  // the situation warrants, so it is surfaced locally instead — Settings.jsx
  // (BillingTab's downgradeScheduledTitle/Body notice) and Plans.jsx (the
  // target card's "Scheduled · <date>" badge + undo), both already gated on
  // this same accessState via resolveScheduledDowngrade.
  if (!accessState || !['grace', 'lapsed', 'cancel_scheduled'].includes(accessState)
    || (accessState === 'lapsed' && !entitlementsError && freePublishingState(entitlements))) return null;

  if (accessState === 'cancel_scheduled') {
    if (dismissedCancellationKey === cancellationKey) return null;
    const until = formatDate(entitlements?.nextInvoiceAt || entitlements?.currentPeriodEnd, i18n.language)
      || t('settings.endOfCurrentPeriod');
    const handleResume = async () => {
      if (resuming) return;
      const scope = captureEntitlementsScope();
      setResuming(true);
      setResumeError(null);
      try {
        if (!isResumeSubmitted(scope)) {
          await api.billing.resumeSubscription();
          if (!isEntitlementsScopeCurrent(scope)) return;
          markResumeSubmitted(scope);
        }
        const result = await refreshEntitlements({
          until: subscriptionResumeHasConverged,
          scope,
        });
        if (!isEntitlementsScopeCurrent(scope)) return;
        if (!result.converged && !result.cancelled) setResumeError(t('settings.billingStatusPending'));
      } catch (_) {
        if (isEntitlementsScopeCurrent(scope)) setResumeError(t('paymentIssue.portalError'));
      } finally {
        if (isEntitlementsScopeCurrent(scope)) setResuming(false);
      }
    };
    return (
      <section className="card flb-banner flb-cancel-scheduled" role="status">
        <span className="flb-ic"><Icon name="alert" size={14} /></span>
        <div className="flb-msg">
          <strong>{t('settings.subscriptionCancelsPrefix')} {until}.</strong>
          <div>{t('settings.subscriptionCancelsSuffix')}</div>
          {resumeError && <div className="flb-error">{resumeError}</div>}
        </div>
        <div className="flb-actions">
          {canResumeSubscription(entitlements) && <button type="button" className="btn btn-primary btn-sm flb-cta" onClick={handleResume} disabled={resuming}>
            {resumeSubmitted
              ? t('settings.refreshBillingStatus')
              : (resuming ? t('settings.resuming', { defaultValue: 'Resuming…' }) : t('settings.resumeSubscription', { defaultValue: 'Resume subscription' }))}
          </button>}
          <button
            type="button"
            className="flb-x flb-cancel-dismiss"
            aria-label={t('upgradeModal.close')}
            onClick={() => setDismissedCancellationKey(cancellationKey)}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      </section>
    );
  }

  if (accessState === 'grace') {
    const until = formatDate(entitlements?.graceEndsAt || entitlements?.nextInvoiceAt || entitlements?.currentPeriodEnd, i18n.language)
      || t('settings.endOfCurrentPeriod');
    const payUrl = entitlements?.paymentIssue?.invoiceUrl || null;
    const handlePayNow = () => {
      try { track('grace_banner_pay_now', {}); } catch { /* fail-silent */ }
      if (payUrl) window.open(payUrl, '_blank', 'noopener,noreferrer');
      else openUpgrade({ feature: 'default', variant: 'renew' });
    };
    return (
      <section className="card flb-banner flb-payment-issue" role="alert">
        <span className="flb-ic"><Icon name="alert" size={14} /></span>
        <div className="flb-msg">
          <strong>{t('grace.bannerTitle')}</strong>
          <div>{t('grace.bannerBody', { date: until })}</div>
        </div>
        <div className="flb-actions">
          <button type="button" className="btn btn-primary btn-sm flb-cta" onClick={handlePayNow}>
            {t('grace.payNow')}
          </button>
        </div>
      </section>
    );
  }

  // lapsed
  const previousPlanKey = entitlements?.previousPlan || null;
  const previousPlanName = resolvePlanName(previousPlanKey, { getPlanDisplay, getNewPlanDisplay, newGridActive });
  const handleRenew = () => {
    try { track('lapsed_banner_renew_clicked', {}); } catch { /* fail-silent */ }
    openUpgrade({ feature: 'default', variant: 'renew' });
  };
  return (
    <section className="card flb-banner flb-lapsed" role="alert">
      <span className="flb-ic"><Icon name="lock" size={14} /></span>
      <div className="flb-msg">
        <strong>{t('lapsed.bannerTitle')}</strong>
        <div>{previousPlanName ? t('lapsed.bannerBodyWithPlan', { plan: previousPlanName }) : t('lapsed.bannerBody')}</div>
      </div>
      <div className="flb-actions">
        <button type="button" className="btn btn-primary btn-sm flb-cta" onClick={handleRenew}>
          {previousPlanName ? t('lapsed.renewPlan', { plan: previousPlanName }) : t('lapsed.renew')}
        </button>
      </div>
    </section>
  );
}
